import type { ReactNode } from "react";

/**
 * The boundary between the marketing site and the app surface.
 *
 * Every route into /session is reached from a public page, and a
 * React-Router `<Link>` does not load a document — it swaps the content
 * in place. That means a visitor who lands on /pricing and clicks
 * through is uploading a denial letter inside the *same document* that
 * loaded the Tag Manager container, with the container still resident
 * and still holding DOM access. Not pushing our own page view does not
 * help; GA4's enhanced measurement fires on history changes by itself.
 *
 * A plain anchor makes the browser fetch /session, Render rewrites it to
 * app.html, and app.html is built without ever calling `renderHead()`.
 * The container is then absent rather than suppressed — a structural
 * guarantee instead of a setting in a console we do not control.
 *
 * The cost is a full page load on the primary call to action, paid once,
 * at the moment the visitor stops being anonymous traffic and starts
 * handling their own medical documents. That is the right place to pay
 * it. Rendering an `<a href>` also leaves the markup identical to what
 * `<Link>` produced, so nothing changes for crawlers — /session stays
 * robots.txt-disallowed exactly as before.
 *
 * `lib/appEntry.ts` holds the imperative form, for handlers that do work
 * before leaving (the header's "Start" button resets the store first).
 */
interface AppEntryLinkProps {
  to: string;
  className?: string;
  children: ReactNode;
  "aria-label"?: string;
}

export function AppEntryLink({ to, className, children, ...rest }: AppEntryLinkProps) {
  return (
    <a href={to} className={className} {...rest}>
      {children}
    </a>
  );
}
